import { defineStore } from 'pinia';
import { useQuery } from '@tanstack/vue-query';
import { SocketEvents, StorageKeys } from '@/helpers/enum';
import httpService from '@/services/http.service';
import storageService from "@/services/storage.service";
import NotificationSocketBuilder from '@/components/notifications-old/utils/notification-socket.builder';

import {
  allTabs,
  subTabs,
  GradientNotification,
  MAX_NOTIFICATIONS,
  NotificationPagination,
  NotificationType,
  NotificationTypeToTab,
  PostNotification,
  isPostNotification,
  getNotificationSubTab,
  NotificationCounts,
  Notification,
  MainTab,
  SubTab,
  NotificationCurrentTabCounts
} from '../types/notification.types';

interface NotificationState {
  notifications: Notification[];
  preFilteredNotifications: Record<MainTab, Notification[]>;
  preFilteredSubNotifications: Record<string, Record<SubTab, Notification[]>>;
  unreadCount: number;
  currentTab: MainTab;
  currentSubTab: SubTab;
  isLoading: boolean;
  isFetchingMore: boolean;
  pagination: {
    page: number;
    limit: number;
    hasMore: boolean;
  };
  counts: NotificationCounts;
  currentTabCounts: NotificationCurrentTabCounts;
  gradientNotificationData: GradientNotification[];
  socket: NotificationSocketBuilder | null;
  isSocketConnected: boolean;
  lastPongReceived: number;
}

export const useNotificationStore = defineStore('notifications', {
  state: (): NotificationState => ({
    notifications: [],
    preFilteredNotifications: {
      All: [],
      Post: [],
      Request: [],
      Conversation: [],
      System: []
    },
    preFilteredSubNotifications: {},
    unreadCount: 0,
    currentTab: 'All',
    currentSubTab: 'All',
    isLoading: false,
    isFetchingMore: false,
    pagination: {
      page: 1,
      limit: 20,
      hasMore: true
    },
    counts: {
      totalUnreadCount: 0,
      unreadByMainTab: {
        All: 0,
        Post: 0,
        Request: 0,
        Conversation: 0,
        System: 0
      },
      unreadByMainTabSubTab: {
        All: {
          All: 0,
          Following: 0,
          Friends: 0,
          Circles: 0
        },
        Post: {
          All: 0,
          Following: 0,
          Friends: 0,
          Circles: 0
        },
        Request: {
          All: 0,
          Following: 0,
          Friends: 0,
          Circles: 0
        },
        Conversation: {
          All: 0,
          Following: 0,
          Friends: 0,
          Circles: 0
        },
        System: {
          All: 0,
          Following: 0,
          Friends: 0,
          Circles: 0
        }
      }
    },
    currentTabCounts: {
      All: 0,
      Following: 0,
      Friends: 0,
      Circles: 0
    },
    gradientNotificationData: [],
    socket: null,
    isSocketConnected: false,
    lastPongReceived: 0,
  }),

  getters: {
    allTabs(): MainTab[] {
      return [...allTabs];
    },

    subTabs(): SubTab[] {
      return [...subTabs];
    },

    shouldShowSubTabs(state): boolean {
      return state.currentTab !== 'System';
    },

    filteredNotifications(state): Notification[] {
      let baseNotifications: Notification[] = [];
      
      if (state.currentTab === 'All') {
        baseNotifications = state.notifications.filter(n => !n.isDeleted);
      } else {
        baseNotifications = (state.preFilteredNotifications[state.currentTab] || []).filter(n => !n.isDeleted);
      }

      // Deduplicate notifications by notificationId to prevent duplicate entries
      const uniqueNotifications = baseNotifications.filter((notification, index, self) => 
        index === self.findIndex(n => n.notificationId === notification.notificationId)
      );

      if (state.currentSubTab === 'All' || !this.shouldShowSubTabs) {       
        return uniqueNotifications;
      }

      return uniqueNotifications.filter(notification => {
        // NEW APPROACH: Check if notification belongs to current subtab
        // This allows notifications to appear in multiple tabs
        let shouldInclude = false;
        
        switch (state.currentSubTab) {
          case 'All':
            shouldInclude = true;
            break;
          case 'Friends':
            // First check new flags, then fallback to original logic
            if (notification.from_friends === true) {
              shouldInclude = true;
            } else {
              // Fallback: use original type-based mapping
              const notificationSubTab = this.getNotificationSubTabBasedOnFlags(notification);
              shouldInclude = notificationSubTab === 'Friends';
            }
            break;
          case 'Circles':
            // First check new flags, then fallback to original logic
            if (notification.from_circle === true) {
              shouldInclude = true;
            } else {
              // Fallback: use original type-based mapping
              const notificationSubTab = this.getNotificationSubTabBasedOnFlags(notification);
              shouldInclude = notificationSubTab === 'Circles';
            }
            break;
          case 'Following':
            // First check new flags, then fallback to original logic
            if (notification.from_follower === true) {
              shouldInclude = true;
            } else {
              // Fallback: use original type-based mapping
              const notificationSubTab = this.getNotificationSubTabBasedOnFlags(notification);
              shouldInclude = notificationSubTab === 'Following';
            }
            break;
          default:
            shouldInclude = false;
        }
        
        return shouldInclude;
      });
    },

    displayedNotifications(state): Notification[] {
      return this.filteredNotifications.slice(0, state.pagination.page * state.pagination.limit);
    },

    hasMoreNotifications(state): boolean {
      return state.pagination.hasMore;
    },

    currentTabSubTabCounts(): (type: SubTab) => number {
      return (type: SubTab) => {
        return this.currentTabCounts[type] || 0;
      };
    },
    // currentTabSubTabCounts(): (type: SubTab) => number {
    //   return (type: SubTab) => {
    //     if (this.currentTab === 'All') {
    //       return this.counts.bySubTab[type] || 0;
    //     }
    //     const notifications = this.preFilteredNotifications[this.currentTab] || [];
    //     return notifications.filter(n => this.getNotificationSubTabBasedOnFlags(n) === type).length;
    //   };
    // },

    currentTabUnreadSubTabCounts(): (type: SubTab) => number {
      return (type: SubTab) => {
        return this.currentTabCounts[type] || 0;
      };
    }
    // currentTabUnreadSubTabCounts(): (type: SubTab) => number {
    //   return (type: SubTab) => {
    //     if (this.currentTab === 'All') {
    //       return this.counts.unreadBySubTab[type] || 0;
    //     }
    //     const notifications = this.preFilteredNotifications[this.currentTab] || [];
    //     return notifications.filter(n => this.getNotificationSubTabBasedOnFlags(n) === type && !n.isRead).length;
    //   };
    // }
  },

  actions: {
    // Helper to determine sub-tab based on the three boolean flags
    getNotificationSubTabBasedOnFlags(notification: Notification): SubTab {
      // For System tab, always return 'All'
      if (NotificationTypeToTab[notification.notificationNewType] === 'System') {
        return 'All';
      }

      if (notification.from_circle) {
        return 'Circles';
      } else if (notification.from_friends) {
        return 'Friends';
      } else if (notification.from_follower) {
        return 'Following';
      }

      // Fallback to the original type-based mapping
      return getNotificationSubTab(notification);
    },

    // Helper to determine main tab based on notificationNewType if available
    getNotificationMainTab(notification: Notification): MainTab {
      // Use notificationNewType if available, otherwise fallback to notificationType
      const effectiveType = notification.notificationNewType;
      return NotificationTypeToTab[effectiveType] || 'All';
    },

    updateNotification(notificationId: string, updates: Partial<Notification>) {
      this.notifications = this.notifications.map(notification =>
        notification._id === notificationId
          ? { ...notification, ...updates, data: { ...notification.data, ...updates.data } }
          : notification
      );

      this.preFilterNotifications();
      this.preFilterSubNotifications();
      this.recalculateCounts();

    },

    async loadOfflineNotifications() {
      try {
        const offlineNotifications = JSON.parse(
          storageService.GetProperty(StorageKeys.offlineAppNotifications) || '[]'
        );

        for (const notification of offlineNotifications) {
          const appNotification = this.transformToAppNotification(notification);
          this.addNotification(appNotification);

          if (isPostNotification(appNotification)) {
            this.showGradientNotification(appNotification as PostNotification);
          }
        }

        this.recalculateCounts();
        this.recalculateCurrentTabCounts();
        storageService.RemoveProperty(StorageKeys.offlineAppNotifications);
      } catch (error) {
        console.error('Failed to load offline notifications:', error);
      }
    },

    transformToAppNotification(notification: any): Notification {
      return {
        _id: notification.messageId || notification.id,
        notificationId: notification.messageId || notification.id,
        notificationType: notification.data?.type || notification.notificationNewType || NotificationType.POST_REGULAR,
        notificationNewType: notification.notificationNewType || notification.data?.newType || '',
        title: notification.notification?.title || notification.title,
        body: notification.notification?.body || notification.body,
        data: notification.data || notification,
        isRead: notification.isRead || false,
        isViewed: notification.isRead || false,
        createdOn: notification.receivedAt || notification.createdAt || new Date().toISOString(),
        isOffline: true,
        isDeleted: notification.isDeleted || false,
        userId: notification.userId || undefined,
        from_friends: notification.from_friends || notification.data?.from_friends || false,
        from_follower: notification.from_follower || notification.data?.from_follower || false,
        from_circle: notification.from_circle || notification.data?.from_circle || false,
      } as Notification;
    },

    showGradientNotification(notification: Notification) {
      this.hideGradientNotification(notification._id);

      const notif: GradientNotification = {
        notification,
        showNotification: true,
        timeOut: setTimeout(() => {
          this.hideGradientNotification(notification._id);
        }, 5000)
      };

      if (this.gradientNotificationData.length >= MAX_NOTIFICATIONS) {
        const oldest = this.gradientNotificationData.shift();
        if (oldest) clearTimeout(oldest.timeOut);
      }

      this.gradientNotificationData.push(notif);
    },

    hideGradientNotification(notificationId: string) {
      const index = this.gradientNotificationData.findIndex(
        n => n.notification.notificationId === notificationId
      );

      if (index > -1) {
        clearTimeout(this.gradientNotificationData[index].timeOut);
        this.gradientNotificationData.splice(index, 1);
      }
    },

    async initializeSocket() {
      if (this.socket?.getConnectionState() === 'connected') {
        return;
      }

      try {
        const userId = storageService.user.value?._id;
        if (!userId) {
          console.error('User Is not authenticated. Cannot initialize notification socket.');
          return;
        }

        await this.loadOfflineNotifications();

        const socketInstance = NotificationSocketBuilder.getInstance()
          .initialize(userId)
          .on(SocketEvents.NEW_NOTIFICATION, this.handleNewNotification)
          .on(SocketEvents.NOTIFICATION_READ, this.handleNotificationRead)
          .on(SocketEvents.NOTIFICATION_COUNT_UPDATE, this.handleCountUpdate)
          .on(SocketEvents.PONG, this.handlePong)
          .on(SocketEvents.ping, this.handlePing);

        this.socket = socketInstance;

        await this.socket.connect();
        this.isSocketConnected = true;

      } catch (error) {
        console.error('Failed to initialize notification WebSocket:', error);
        this.isSocketConnected = false;
        this.socket = null;
        await this.loadOfflineNotifications();
        throw error;
      }
    },

    cleanup() {
      if (this.socket) {
        this.socket.cleanup();
        this.socket = null;
      }
      this.isSocketConnected = false;
    },

    handleNewNotification(notification: Notification) {
      this.addNotification(notification);
      this.showGradientNotification(notification);

    },

    handlePong(data: any) {
      this.lastPongReceived = Date.now();
    },

    handlePing(data: any) {
      if (this.socket) {
        this.socket.emit('pong', {
          timestamp: Date.now(),
          receivedPing: data
        });
      }
    },

    handleNotificationRead(ids: string[]) {
      this.markAsRead(ids);
    },

    handleCountUpdate(counts: NotificationCounts) {
      this.updateCounts(counts);
      // this.recalculateCurrentTabCounts();
    },

    updatePagination(pagination: NotificationPagination) {
      this.pagination.page = pagination.currentPage;
      this.pagination.limit = pagination.itemsPerPage;
      this.pagination.hasMore = pagination.hasNext;
    },
    async recalculateCounts() {
      // Check if user is authenticated before making API call
      const token = storageService.getToken();
      const user = storageService.getUser();
      if (!token || !user || !user._id) {
        return; // Don't make API call if user is not authenticated
      }
      
      try {
        const response = await httpService.fetchNotificationCounts();
        if (response?.data) {
          this.updateCounts(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch notification counts:', error);
      }
    },
    async fetchInitialNotifications() {
      // Check if user is authenticated before making API call
      const token = storageService.getToken();
      const user = storageService.getUser();
      if (!token || !user || !user._id) {
        this.isLoading = false;
        return; // Don't make API call if user is not authenticated
      }
      
      this.isLoading = true;
      try {
        const response = await httpService.fetchNotifications(this.pagination.page, this.pagination.limit);

        if (!response) {
          console.warn('No response received from API');
          return;
        }

        const responseData = response.data;
        if (!responseData || !responseData.data) {
          console.warn('No notifications found in the response data');
          return;
        }

        this.notifications = responseData.data;

        if (responseData.pagination) {
          this.updatePagination(responseData.pagination);
        }

        this.preFilterNotifications();
        this.preFilterSubNotifications();
        this.recalculateCounts();
        // this.recalculateCurrentTabCounts();

        return responseData;
      } catch (e: any) {
        console.error('Error in fetchInitialNotifications:', e.message);
        console.error('Full error:', e);
        throw e;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchMoreNotifications() {
      // Check if user is authenticated before making API call
      const token = storageService.getToken();
      const user = storageService.getUser();
      if (!token || !user || !user._id) {
        return; // Don't make API call if user is not authenticated
      }
      
      if (!this.pagination.hasMore || this.isFetchingMore) return;

      this.isFetchingMore = true;
      try {
        const nextPage = this.pagination.page + 1;
        const { data } = await httpService.fetchNotifications(nextPage, this.pagination.limit);

        const newNotifications = data.data || [];
        this.notifications = [...this.notifications, ...newNotifications];

        this.preFilterNotifications();
        this.preFilterSubNotifications();
        this.recalculateCounts();
        // this.recalculateCurrentTabCounts();

        this.updatePagination({
          ...data.pagination,
          page: nextPage
        });

      } catch (error) {
        console.error('Failed to fetch more notifications:', error);
      } finally {
        this.isFetchingMore = false;
      }
    },

    preFilterNotifications() {
      const result: Record<MainTab, Notification[]> = {
        All: [],
        Post: [],
        Request: [],
        Conversation: [],
        System: []
      };

      this.notifications.forEach(notification => {
        if (notification.isDeleted) {
          return;
        }

        const tab = this.getNotificationMainTab(notification);
        if (tab && result[tab]) {
          result[tab].push(notification);
        }
        result.All.push(notification);
      });
      
      this.preFilteredNotifications = result;
    },

    preFilterSubNotifications() {
      const result: Record<string, Record<SubTab, Notification[]>> = {};

      (['Post', 'Request', 'Conversation', 'System'] as MainTab[]).forEach(mainTab => {
        const tabNotifications = this.preFilteredNotifications[mainTab] || [];
        const subTabResult: Record<SubTab, Notification[]> = {
          All: tabNotifications,
          Following: [],
          Friends: [],
          Circles: []
        };

        tabNotifications.forEach(notification => {
          if (notification.isDeleted) {
            return;
          }

          const subTab = this.getNotificationSubTabBasedOnFlags(notification);
          if (subTabResult[subTab]) {
            subTabResult[subTab].push(notification);
          }
        });

        result[mainTab] = subTabResult;
      });

      this.preFilteredSubNotifications = result;
    },

    addNotification(notification: Notification) {
      if (!this.notifications.some(n => n.notificationId === notification.notificationId)) {
        this.notifications.unshift(notification);

        const mainTab = this.getNotificationMainTab(notification);

        // Update main tab pre-filtering
        if (mainTab && this.preFilteredNotifications[mainTab]) {
          this.preFilteredNotifications[mainTab].unshift(notification);
        }
        this.preFilteredNotifications.All.unshift(notification);

        // Update sub-tab pre-filtering
        if (mainTab && this.preFilteredSubNotifications[mainTab]) {
          const subTab = this.getNotificationSubTabBasedOnFlags(notification);

          if (this.preFilteredSubNotifications[mainTab][subTab]) {
            this.preFilteredSubNotifications[mainTab][subTab].unshift(notification);
          }
          this.preFilteredSubNotifications[mainTab].All.unshift(notification);
        }
        this.recalculateCounts();

      }
    },

    removeNotification(id: string) {
      const notification = this.notifications.find(n => n.notificationId === id);
      if (notification) {
        const mainTab = this.getNotificationMainTab(notification);

        // Remove from main tab filtering
        if (mainTab && this.preFilteredNotifications[mainTab]) {
          this.preFilteredNotifications[mainTab] =
            this.preFilteredNotifications[mainTab].filter(n => n.notificationId !== id);
        }
        this.preFilteredNotifications.All =
          this.preFilteredNotifications.All.filter(n => n.notificationId !== id);

        // Remove from sub-tab filtering
        if (mainTab && this.preFilteredSubNotifications[mainTab]) {
          const subTab = this.getNotificationSubTabBasedOnFlags(notification);

          if (this.preFilteredSubNotifications[mainTab][subTab]) {
            this.preFilteredSubNotifications[mainTab][subTab] =
              this.preFilteredSubNotifications[mainTab][subTab].filter(n => n.notificationId !== id);
          }
          this.preFilteredSubNotifications[mainTab].All =
            this.preFilteredSubNotifications[mainTab].All.filter(n => n.notificationId !== id);
        }
      }

      this.notifications = this.notifications.filter(n => n.notificationId !== id);
      this.recalculateCounts();
      // this.recalculateCurrentTabCounts();
    },

    async markAsRead(ids: string[]): Promise<boolean> {
      try {
        if (ids.length === 0) return false;
        this.isLoading = true;

        // Optimistic update


        httpService.markNotificationAsRead(ids).then(
          () => {
            this.isLoading = false;
            this.recalculateCounts();
            // this.recalculateCurrentTabCounts();
            this.notifications = this.notifications.map(n =>
              ids.includes(n._id) ? { ...n, isRead: true } : n
            );
            this.preFilterNotifications();
            this.preFilterSubNotifications();
          },
          (error) => {
            this.isLoading = false;
            console.error('Failed to mark notifications as read on server:', error);

          }
        );

        return true;
      } catch (error) {
        console.error('Failed to mark notifications as read1:', error);
        return false;
      }
    },

    async markAllAsRead() {
      const unreadIds = this.notifications
        .filter(n => !n.isRead)
        .map(n => n._id);

      await this.markAsRead(unreadIds);
    },

    async markAllAsReadNew(): Promise<boolean> {
      try {
        this.isLoading = true;

        const response = await httpService.markAllNotificationsAsRead();
        
        if (response) {
          this.isLoading = false;
          
          // Mark all notifications as read in local state
          this.notifications = this.notifications.map(n => ({ ...n, isRead: true }));
          
          // Recalculate filters and counts
          this.preFilterNotifications();
          this.preFilterSubNotifications();
          this.recalculateCounts();
          
          return true;
        }
        
        this.isLoading = false;
        return false;
      } catch (error) {
        this.isLoading = false;
        console.error('Failed to mark all notifications as read:', error);
        return false;
      }
    },

    async markAllByType(tab: MainTab) {
      const unreadIds = this.notifications
        .filter(n => {
          if (tab === 'All') return !n.isRead;
          const notificationTab = this.getNotificationMainTab(n);
          return !n.isRead && notificationTab === tab;
        })
        .map(n => n._id);

      if (unreadIds.length > 0) {
        await this.markAsRead(unreadIds);
      }
    },

    updateCounts(countsData: NotificationCounts) {
      this.counts = countsData;
      this.unreadCount = countsData.totalUnreadCount;
      this.recalculateCurrentTabCounts();
    },

    recalculateCurrentTabCounts() {
      const currentTab = this.currentTab;
      const subtabCounts = this.counts.unreadByMainTabSubTab[currentTab] || {
        All: 0, Following: 0, Friends: 0, Circles: 0
      };

      this.currentTabCounts = subtabCounts;
    },

    setCurrentTab(tab: MainTab) {
      this.currentTab = tab;
      this.currentSubTab = 'All';
      this.pagination.page = 1;
      this.recalculateCurrentTabCounts();
    },

    setCurrentSubTab(tab: SubTab) {
      this.currentSubTab = tab;
      this.pagination.page = 1;
    }
  }
});

export const useNotificationQuery = () => {
  const store = useNotificationStore();

  const { isLoading, data } = useQuery({
    queryKey: ['notifications', 'initial'],
    queryFn: () => store.fetchInitialNotifications(),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    isLoading,
    data,
    store
  };
};